/**
 * SS Domain Components — healthcare-specific badges and display helpers
 * ─────────────────────────────────────────────────────────────────────────────
 * These are built on top of the primitive Badge so styling stays consistent.
 *
 * Usage:
 *   import { PartyTypePill, UserStatusBadge, PermissionResultBadge,
 *            EventTypeBadge, BreakGlassBadge, TwoFaBadge } from './domain';
 */
import React from 'react';
import { Badge } from './primitives';
import { COLOR, FONT } from './tokens';

// ── PartyTypePill ─────────────────────────────────────────────────────────────
/** PER → 👤 Person (blue) | ORG → 🏢 Org (purple) */
export function PartyTypePill({ type }) {
  if (!type) return <Badge color="gray">—</Badge>;
  return type === 'PER'
    ? <Badge color="blue">👤 Person</Badge>
    : <Badge color="purple">🏢 Org</Badge>;
}

// ── ActiveBadge ───────────────────────────────────────────────────────────────
/** Generic active / inactive / closed badge */
export function ActiveBadge({ active, labels = ['Active', 'Inactive'] }) {
  return active
    ? <Badge color="green">{labels[0]}</Badge>
    : <Badge color="red">{labels[1]}</Badge>;
}

// ── UserStatusBadge ───────────────────────────────────────────────────────────
/** Derives user status from ssusers flags */
export function UserStatusBadge({ user }) {
  if (!user) return null;
  const isLocked = user.lockoutenddateutc && new Date(user.lockoutenddateutc) > new Date();
  if (isLocked)       return <Badge color="amber">🔒 Locked</Badge>;
  if (!user.isenabled) return <Badge color="red">Disabled</Badge>;
  return <Badge color="green">Active</Badge>;
}

// ── PermissionResultBadge ─────────────────────────────────────────────────────
/** isgranted → ✓ Granted | ✕ Denied */
export function PermissionResultBadge({ isGranted }) {
  if (isGranted === true)  return <Badge color="green">✓ Granted</Badge>;
  if (isGranted === false) return <Badge color="red">✕ Denied</Badge>;
  return <Badge color="gray">—</Badge>;
}

// ── EventTypeBadge ────────────────────────────────────────────────────────────
/** Colour-codes event type by namespace prefix */
export function EventTypeBadge({ eventType }) {
  if (!eventType) return <Badge color="gray">—</Badge>;
  const lower = eventType.toLowerCase();
  if (lower.startsWith('auth.'))          return <Badge color="blue">{eventType}</Badge>;
  if (lower.startsWith('authz.'))         return <Badge color="purple">{eventType}</Badge>;
  if (lower.startsWith('compliance.'))    return <Badge color="red">{eventType}</Badge>;
  if (lower.startsWith('parties.'))       return <Badge color="teal">{eventType}</Badge>;
  if (lower.startsWith('relationships.')) return <Badge color="green">{eventType}</Badge>;
  return <Badge color="gray">{eventType}</Badge>;
}

// ── BreakGlassBadge ───────────────────────────────────────────────────────────
export function BreakGlassBadge({ isBreakGlass }) {
  return isBreakGlass
    ? <Badge color="amber">🚨 Break-Glass</Badge>
    : <Badge color="gray">Normal</Badge>;
}

// ── TwoFaBadge ────────────────────────────────────────────────────────────────
export function TwoFaBadge({ enabled }) {
  return enabled ? <Badge color="blue">2FA On</Badge> : <Badge color="gray">2FA Off</Badge>;
}

// ── SystemRoleBadge ───────────────────────────────────────────────────────────
export function SystemRoleBadge({ isSystem }) {
  return isSystem ? <Badge color="teal">System</Badge> : null;
}

// ── PartyRoleChips ────────────────────────────────────────────────────────────
/** Renders an array of party role objects as coloured chips */
export function PartyRoleChips({ roles = [] }) {
  if (!roles.length) return <span style={{ color: COLOR.grayLight, fontSize: FONT.size.sm }}>—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {roles.map(r => (
        <Badge key={r.code || r.partyroleid} color="green" style={{ fontSize: 10 }}>
          {r.code}
        </Badge>
      ))}
    </div>
  );
}

// ── PermissionEffectBadge ─────────────────────────────────────────────────────
/** allow / deny badge for permission rows */
export function PermissionEffectBadge({ isAllow }) {
  return isAllow
    ? <Badge color="green">Allow</Badge>
    : <Badge color="red">Deny</Badge>;
}

// ── SensitivityBadge ──────────────────────────────────────────────────────────
const SENSITIVITY_COLORS = {
  PHI_GENERAL:     'red',
  PHI_MENTAL_HEALTH:'red',
  PHI_HIV:         'red',
  PHI_SUBSTANCE_USE:'red',
  PHI_GENETIC:     'red',
  PHI_REPRODUCTIVE:'red',
  FINANCIAL:       'amber',
  PII_HIGH:        'amber',
  PII_STANDARD:    'blue',
  PUBLIC:          'gray',
};
export function SensitivityBadge({ tag }) {
  const color = SENSITIVITY_COLORS[tag] || 'gray';
  return <Badge color={color}>{tag}</Badge>;
}
